import React, { useEffect, useState } from 'react';
import {
  requiredField,
  intialPageSize,
  spedNoteSignedOffSuccess,
} from 'constants';
import * as yup from 'yup';
import {
  renderBulkSignOffSaveButton,
  bulkSignOffSpedNotesColumns,
  bulkSignOffSpedNotesListData,
} from './bulkSignOffUtils';
import {
  getSnackBar,
  getOptionLabel,
  useNotification,
  pageSizeChangeHandler,
} from 'utils/commonUtils';
import MDBox from 'components/MDBox';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { GridForm } from 'components/Ui/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  postBulkSignOffSpedNote,
  getAllSpedNotesForBulkSignOff,
  getStudentSpedNotesForBulkSignOff,
} from 'layouts/spedNotes/spedNotes/services/spedNotesApi';
import { SignatureBlock } from '../../spedNotesCommonUtils';
import DeltaDataGrid from 'utils/CommonDataGrid/CommonDataGrid';
import { displaySnackbar } from 'components/snackbar/store/SnackbarSlice';
import { getSearchedStudents } from 'layouts/students/service/studentsApi';
import { FormInputAutoCompleteSearch } from 'components/Common/Forms/FormAutoCompleteSearch';

const BulkSignOffComponent = (props) => {
  const { setBulkSignOff } = props;
  const dispatch = useDispatch();

  const signatureTextValidation = yup
    .string()
    .required(requiredField)
    .matches(/^[a-zA-Z\s]+$/, 'This signature must contain only letters');

  const signatureAgreeValidation = yup
    .boolean()
    .oneOf([true], '*You must agree before submitting');

  const schema = React.useMemo(
    () =>
      yup.object().shape({
        seifEsignature: signatureTextValidation,
        seifSignatureAgree: signatureAgreeValidation,
      }),
    [],
  );

  const { handleErrorResponse, NotificationPopup } = useNotification();

  const [student, setStudent] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [isStudentSearchLoading, setIsStudentSearchLoading] = useState(false);
  const [bulkSignOffButtonLoading, setIsBulkSignOffButtonLoading] =
    useState(false);
  const [spedNotesList, setSpedNotesList] = useState({
    rows: [],
    isLoading: false,
    pageSize: intialPageSize,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (studentSearchQuery?.length >= 1) {
        fetchSearchedStudents();
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [studentSearchQuery]);

  useEffect(() => {
    fetchAllUnsignedNotes();
  }, []);

  const fetchSearchedStudents = async () => {
    try {
      setIsStudentSearchLoading(true);

      const searchedStudents = await getSearchedStudents(
        studentSearchQuery,
        true,
        false,
        20,
      );

      setStudentOptions(searchedStudents?.items || []);
      setIsStudentSearchLoading(false);
    } catch (error) {
      setIsStudentSearchLoading(false);
    }
  };

  const handleStudentSelect = (val) => {
    setStudent(val);
    setSelectedIds([]);

    if (val?.studentId) {
      fetchSpedNotesForStudent(val.studentId);
    } else {
      setSpedNotesList((prev) => ({ ...prev, rows: [] }));
      fetchAllUnsignedNotes();
    }
  };

  const loadSpedNotes = async (apiCall) => {
    try {
      setSpedNotesList((prev) => ({ ...prev, isLoading: true }));

      const response = await apiCall();

      setSpedNotesList((prev) => ({
        ...prev,
        rows: bulkSignOffSpedNotesListData(response),
        isLoading: false,
      }));
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
      setSpedNotesList((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const fetchAllUnsignedNotes = () => {
    return loadSpedNotes(getAllSpedNotesForBulkSignOff);
  };

  const fetchSpedNotesForStudent = (studentId) => {
    return loadSpedNotes(() => getStudentSpedNotesForBulkSignOff(studentId));
  };

  const pageSizeChange = pageSizeChangeHandler(setSpedNotesList);

  const defaultValue = {
    seifEsignature: '',
    seifSignatureAgree: false,
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
    resolver: yupResolver(schema),
  });

  const { handleSubmit, control, formState, watch } = useFunction;
  const { errors } = formState;

  const watchedAgree = watch('seifSignatureAgree');
  const watchedSignature = watch('seifEsignature');

  const onSubmit = async (data) => {
    setIsBulkSignOffButtonLoading(true);
    const { seifEsignature = '', seifSignatureAgree = false } = data;

    const payload = {
      noteIds: selectedIds,
      seifEsignature: seifEsignature,
      seifSignatureAgree: seifSignatureAgree,
    };

    const snackBar = getSnackBar(spedNoteSignedOffSuccess);
    try {
      await postBulkSignOffSpedNote(payload);
      setBulkSignOff(false);
      dispatch(displaySnackbar(snackBar));
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    } finally {
      setIsBulkSignOffButtonLoading(false);
    }
  };

  const disableSave =
    selectedIds?.length === 0 || !watchedSignature || !watchedAgree;

  return (
    <>
      <GridForm container spacing={2}>
        <Grid item xs={12} sm={4}>
          <FormInputAutoCompleteSearch
            value={student}
            options={studentOptions}
            getOptionLabel={getOptionLabel}
            placeholder="Enter name or ID..."
            setSelectedInput={handleStudentSelect}
            label="Search for student by name or ID"
            isSearchloading={isStudentSearchLoading}
            setInputSearchQuery={setStudentSearchQuery}
          />
        </Grid>
      </GridForm>

      <MDBox mb={1} />
      <GridForm container spacing={2}>
        <DeltaDataGrid
          listData={spedNotesList}
          defaultSortField="lastName"
          setListData={pageSizeChange}
          columns={bulkSignOffSpedNotesColumns(selectedIds, setSelectedIds)}
        />
      </GridForm>

      <MDBox mb={2.5} />
      <GridForm container spacing={2}>
        <Grid container>
          <Grid item xs={12}>
            <SignatureBlock
              errors={errors}
              control={control}
              inputName="seifEsignature"
              title="SEIF Electronic Signature"
              checkboxName="seifSignatureAgree"
              inputLabel="SEIF Electronic Signature"
              inputPlaceholder="Enter SEIF Signature"
            />
          </Grid>
        </Grid>
        <Grid container>
          {renderBulkSignOffSaveButton(
            handleSubmit(onSubmit),
            bulkSignOffButtonLoading,
            disableSave,
          )}
        </Grid>
      </GridForm>
      <NotificationPopup />
    </>
  );
};

export default BulkSignOffComponent;
